/**
 * The cart drawer opens MODAL at every width, and every route in and out of it
 * animates.
 *
 * WHY THIS EXISTS. Client, 2026-09-07: *"header is over cart now, doesn't close
 * when I click on page, let's also add gray opacity overlay on site when drawer
 * is open"* and *"when drawer is open I can't click on anything in 'brands we
 * love' submenu"*.
 *
 * All four are one fact. `assets/theme-drawer.js` (untouched Horizon) gates
 * modality on `#modalQuery = (max-width: MODAL_BREAKPOINT - 1px)`: modal on
 * mobile, `panel.show()` on desktop. A non-modal `<dialog>` is not in the top
 * layer, so:
 *   - the sticky header (z-index 18) paints over it,
 *   - there is no `::backdrop` pseudo-element to tint,
 *   - nothing dismisses it when you click the page,
 *   - and the rest of the page stays interactive underneath, which is why the
 *     mega menu still took clicks while the cart was open.
 *
 * Horizon shipped it that way because its desktop drawer PUSHES the page — with
 * the page narrowed rather than covered, a modal would be wrong. We removed the
 * push (`src/u2-cart-drawer.css`), so the non-modal half is now simply a bug.
 *
 * ADR-006: never edit a Horizon file — stop using its behaviour. Nothing below
 * patches `theme-drawer.js`; every change is made on OUR OWN dialog instance.
 *
 * ── 2026-09-08: THE MISSING OPEN ANIMATION ──────────────────────────────────
 * Client: *"cart drawer come in has no animation? when you click close it does
 * have one, when you click the page to close it doesn't have one"*.
 *
 * The animation itself moved to `src/u2-cart-drawer.css`, bound to the dialog's
 * `[open]` state rather than to any handler, so no route can skip it. That doc
 * block carries the sampled frames for all three of the client's cases. Two
 * things in THIS file were part of the cause, and both are fixed here.
 *
 * 1. THE CLOSE-THEN-REOPEN IS GONE. This module used to wait for the `open`
 *    attribute to appear and then `close()` + `showModal()` to re-open modally.
 *    A dialog closed and re-opened inside one task never has an entry
 *    transition to run — the element is rendered on both sides of it, so
 *    `@starting-style` has nothing to trigger on. `show()` is redefined on the
 *    instance instead, so Horizon's one desktop `panel.show()` call opens the
 *    dialog modally in a single step. It is an own property shadowing
 *    `HTMLDialogElement.prototype`, not an edit to a Horizon file and not a
 *    prototype patch that could reach another theme's dialogs.
 *
 * 2. LIGHT DISMISS GOES THROUGH THE COMPONENT. The backdrop-click handler below
 *    called `dialog.close()` directly, and it is registered before Horizon's own
 *    `#onBackdropClick`, so it won the click; Horizon's handler then found
 *    `panel.open` already false and skipped its entire close path. Measured: one
 *    `close` event at 6ms, `display: none` on the next frame, `getAnimations()`
 *    empty. It now asks the `<theme-drawer>` to close, which is the same route
 *    the ✕ takes — so the two cannot diverge again — and falls back to closing
 *    the dialog directly if the custom element has not upgraded.
 *
 * The MutationObserver stays as the belt to that brace. The restore path in
 * `snippets/theme-drawer.liquid` sets `[open]` from an inline script without
 * ever calling `show()`, which leaves a dialog that is open but not in the top
 * layer; `promote()` is what catches that. `:modal` is the authoritative test —
 * true only for a dialog in the top layer — so it cannot fight the mobile path,
 * where Horizon already called `showModal()` and the check simply passes.
 *
 * `closedby="any"` asks the platform for light dismiss (click outside / Esc).
 * Where it is unsupported the explicit handler below does the same job, so the
 * behaviour does not depend on it.
 */

function promote(dialog) {
  if (!dialog.open || dialog.matches(':modal')) return;
  // Re-opening fires no extra `open` mutation we care about: the attribute is
  // already present, so the observer below does not loop. Both calls are in one
  // task, so the panel is never rendered non-modally in between and the entry
  // transition does not fire on a drawer that was already on screen.
  dialog.close();
  try {
    dialog.showModal();
  } catch {
    // A dialog already in the top layer throws; nothing to do if it is.
  }
}

/** Closes via the `<theme-drawer>` so the component's own close path runs. */
function dismiss(dialog) {
  const host = dialog.closest('theme-drawer');

  // `hasAttribute('open')` because `ThemeDrawer#close()` is a no-op when the
  // host does not consider itself open — and the restore path can leave the
  // dialog open with the host lagging. Falling through to `dialog.close()`
  // there keeps light dismiss working whatever state the component is in.
  if (host && typeof host.close === 'function' && host.hasAttribute('open')) {
    host.close();
    return;
  }

  dialog.close();
}

function wire(dialog) {
  if (dialog.dataset.rpModal === 'on') return;
  dialog.dataset.rpModal = 'on';
  dialog.setAttribute('closedby', 'any');

  /* Horizon's desktop path is `panel.show()`. One redefined method on this one
     element turns that into a modal open, which is the whole of what the
     observer below used to do after the fact — without the close/re-open that
     cost the drawer its entry transition. The guard matters: `showModal()`
     throws on an already-open dialog, and `#onModalBreakpointChange` calls
     `show()` on a panel it has just closed. */
  dialog.show = function show() {
    if (this.open) return;
    this.showModal();
  };

  new MutationObserver(() => promote(dialog)).observe(dialog, {
    attributes: true,
    attributeFilter: ['open'],
  });

  /* Light dismiss without relying on `closedby`. A click whose target IS the
     dialog element landed on the backdrop: the panel's own children are
     descendants, so any click inside the drawer has a deeper target. */
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dismiss(dialog);
  });

  promote(dialog);
}

function init() {
  for (const dialog of document.querySelectorAll('dialog.rp-cart-drawer')) wire(dialog);
}

init();
document.addEventListener('shopify:section:load', init);
